import axios from "axios";
import type { AxiosError, AxiosResponse } from "axios";

import { env } from "~/env";
import { ROUTES } from "~/router/constants/routes";
import { clearSessionData } from "~/shared/utils/logout";
import { useAuthStore } from "~/stores";
import { ERROR_CODE } from "./api.types";
import {
  authHeaderInterceptor,
  refreshTokenFn,
  refreshTokenInvalidSchema,
} from "./interceptors";

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// This is added because we use sessions to authenticate the user. Therefore, the login and check status endpoints are placed in the web.php routing, rather than in the API routes.
export const web = axios.create({
  baseURL: env.VITE_APP_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(authHeaderInterceptor);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    const isRefreshTokenInvalid = refreshTokenInvalidSchema.safeParse(
      error.response?.data,
    );

    const errorCode = (error.response?.data as { error?: { code?: string } })
      ?.error?.code;
    const shouldBypassInterceptor =
      isRefreshTokenInvalid.success ||
      errorCode === ERROR_CODE.magicLinkExpired;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !shouldBypassInterceptor
    ) {
      originalRequest._retry = true;

      const refreshWasSuccessful = await refreshTokenFn();

      if (refreshWasSuccessful) {
        const accessToken = useAuthStore.getState().accessToken;
        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          return api(originalRequest);
        }
      }

      clearSessionData();
      window.location.href = ROUTES.fullLogin;
    }
    return Promise.reject(error);
  },
);

declare module "axios" {
  interface AxiosRequestConfig {
    _retry?: boolean;
  }
}
